import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { RoutePlanRecord, SalesApiService } from './sales-api.service';

type StatusOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-sales-distribution-routes',
  imports: [AinDatePipe, AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('sales.distribution.routes.title')" [description]="t('sales.distribution.routes.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.distribution.settlements.title')" severity="secondary" (click)="openSettlements()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters">
            <label>
              <span>{{ t('columns.status') }}</span>
              <p-select [options]="statusOptions" optionValue="value" [(ngModel)]="filters.status" [showClear]="true" appendTo="body" (ngModelChange)="load()">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.distribution.routes.fields.routeNumber') }}</th>
                <th>{{ t('sales.distribution.routes.fields.plannedDate') }}</th>
                <th>{{ t('sales.distribution.routes.fields.visits') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.routeNumber }}</button></td>
                <td>{{ row.plannedDateUtc | ainDate }}</td>
                <td>{{ row.completedVisitCount }} / {{ row.visitCount }}</td>
                <td>{{ statusLabel(row.status) }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`.panel{display:grid;gap:10px}.toolbar{display:flex;gap:8px;flex-wrap:wrap}.filters label{display:grid;gap:6px;max-width:220px}.link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}.error{color:#b91c1c}`]
})
export class SalesDistributionRoutesComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<RoutePlanRecord[]>([]);
  readonly selectedRows = signal<RoutePlanRecord[]>([]);
  readonly filters = { status: null as string | null };
  readonly statusOptions: StatusOption[] = [
    { value: 'Draft', labelKey: 'sales.distribution.routes.status.draft' },
    { value: 'Planned', labelKey: 'sales.distribution.routes.status.planned' },
    { value: 'InProgress', labelKey: 'sales.distribution.routes.status.inProgress' },
    { value: 'Completed', labelKey: 'sales.distribution.routes.status.completed' },
    { value: 'Cancelled', labelKey: 'sales.distribution.routes.status.cancelled' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listDistributionRoutes({ status: this.filters.status ?? undefined }).subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.distribution.routes.errorLoad')); this.loading.set(false); }
    });
  }

  onSelectionChange(s: RoutePlanRecord[] | RoutePlanRecord | null): void {
    this.selectedRows.set(Array.isArray(s) ? s : s ? [s] : []);
  }

  openCreate(): void { void this.router.navigate(['/sales/distribution-routes/new']); }
  openSelected(): void { const r = this.selectedRows()[0]; if (r) this.openDetail(r.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/distribution-routes', id]); }
  openSettlements(): void { void this.router.navigate(['/sales/distribution-settlements']); }
  statusLabel(status: string): string {
    const key = `sales.distribution.routes.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.localization.translate(key);
    return translated === key ? status : translated;
  }
  t(key: string): string { return this.localization.translate(key); }
}
